import React from 'react';
import { Dialog, DialogContent, DialogTitle, IconButton, Typography, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import PrintIcon from '@mui/icons-material/Print';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import DownloadIcon from '@mui/icons-material/Download';
import { Document, Page } from 'react-pdf';
import { AsyncLightbox } from 'domain/shared/artifact';
import { Loading } from 'framework/ui/mui-builder';
import useDocumentPreview from './useDocumentPreview';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

interface Props {
  filePath: string | null;
  previewPath: string | null;
  fileName?: string;
  onClose?: () => void;
}

const DocumentPreviewModal: React.FC<Props> = ({ filePath, previewPath, fileName, onClose }) => {
  const {
    open,
    loading,
    blobUrl,
    fileExtension,
    isPreviewAvailable,
    isPreviewImage,
    handleDownload,
    handlePrint,
    handleClose,
    numPages,
    onDocumentLoadSuccess,
    scale,
    zoomIn,
    zoomOut,
  } = useDocumentPreview({ previewPath, filePath, onClose });

  if (loading) {
    return <Loading sx={{ zIndex: 9999 }} hasBackground hasPositionFixed />;
  }

  if (!open || !blobUrl) return null;

  return (
    <>
      {isPreviewAvailable(fileExtension) && (
        <Dialog open fullWidth maxWidth="md" scroll="paper" onClick={e => e.stopPropagation()} onClose={handleClose}>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5">{fileName || filePath?.split('/')?.pop() || 'Document'}</Typography>
            <Box display="flex" gap="10px">
              <IconButton onClick={zoomOut}>
                <ZoomOutIcon />
              </IconButton>
              <IconButton onClick={zoomIn}>
                <ZoomInIcon />
              </IconButton>
              <IconButton onClick={handleDownload}>
                <DownloadIcon />
              </IconButton>
              <IconButton onClick={handlePrint}>
                <PrintIcon />
              </IconButton>
              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
            <Box sx={{ height: '400px' }}>
              <Document file={blobUrl} onLoadSuccess={onDocumentLoadSuccess}>
                {Array.from(new Array(numPages), (_, index) => (
                  <Page key={`page_${index}`} pageNumber={index + 1} width={700} scale={scale} />
                ))}
              </Document>
            </Box>
          </DialogContent>
        </Dialog>
      )}

      {isPreviewImage(fileExtension) && (
        <AsyncLightbox
          open={open}
          onClose={handleClose}
          photos={[{ src: blobUrl }]}
          initial={0}
          onDownload={handleDownload}
        />
      )}
    </>
  );
};

export default DocumentPreviewModal;
